'use client';

import {
    ReactElement,
    ChangeEvent,
    useId,
    useState,
    useEffect,
    KeyboardEvent,
    useRef,
} from 'react';
import styles from './P.module.sass';

type POnChangeEvent = (
    | UIEvent
    | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) & {
    target: (HTMLInputElement | HTMLTextAreaElement) & {
        value: string;
    };
};

type POnKeyDownEvent = KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>;

interface PProps {
    className?: string;
    labelClassName?: string;
    value?: string;
    isEditable?: boolean;
    placeholder?: string;
    children?: ReactElement | ReactElement[] | string;
    onChange?: (value: string) => void;
    onKeyDown?: (event: POnKeyDownEvent) => void;
    rows?: number;
    type?: string;
    pattern?: string;
    enabled?: boolean;
    maxLength?: number;
}

const P = ({
    className = '',
    labelClassName = '',
    value = '',
    isEditable,
    children,
    onChange,
    onKeyDown,
    placeholder,
    rows,
    type = undefined,
    pattern,
    enabled = true,
    maxLength,
    ...props
}: PProps) => {
    const [hasScrolled, setHasScrolled] = useState<boolean>(false);
    const inputId = useId();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const hasValue = !!value;

    const resizeTextArea = (target: HTMLTextAreaElement) => {
        target.style.height = 'inherit';
        target.style.height = `${target.scrollHeight}px`;
    };

    useEffect(() => {
        if (textAreaRef.current && rows === undefined)
            resizeTextArea(textAreaRef.current);
    }, [textAreaRef, isEditable]);

    if (isEditable) {
        const inputClassName = `${styles.p} ${
            isEditable ? styles['p--editable'] : ''
        } ${placeholder && hasValue && styles['p--placeholder']} ${className}`;

        const inputOnChange = (event: POnChangeEvent) => {
            onChange?.(event.target.value);
        };

        return (
            <div className={`relative flex flex-col ${className}`}>
                {!!placeholder && (
                    <label
                        title={placeholder}
                        className={`${styles.label} ${
                            hasValue &&
                            (!hasScrolled
                                ? styles['label--filled']
                                : styles['label--scrolled'])
                        } ${
                            rows === 1 && styles['label--singlerow']
                        } ${labelClassName}`}
                        htmlFor={inputId}
                    >
                        {placeholder}
                    </label>
                )}
                {rows === 1 ? (
                    <input
                        id={inputId}
                        className={inputClassName}
                        onChange={(event) => inputOnChange(event)}
                        onKeyDown={
                            onKeyDown ? (event) => onKeyDown(event) : undefined
                        }
                        value={value}
                        placeholder={placeholder}
                        type={type}
                        pattern={pattern}
                        disabled={!enabled}
                        maxLength={maxLength}
                        {...props}
                    />
                ) : (
                    <textarea
                        ref={textAreaRef}
                        id={inputId}
                        className={inputClassName}
                        onChange={(event) => {
                            inputOnChange(event);
                            resizeTextArea(event.target as HTMLTextAreaElement);
                        }}
                        onKeyDown={(event) => {
                            onKeyDown?.(event);
                            resizeTextArea(event.target as HTMLTextAreaElement);
                        }}
                        value={value}
                        placeholder={placeholder}
                        rows={rows}
                        disabled={!enabled}
                        maxLength={maxLength}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onScroll={(event: any) => {
                            setHasScrolled(
                                (event.target as HTMLTextAreaElement)
                                    .scrollTop > 0,
                            );
                        }}
                        {...props}
                    />
                )}
            </div>
        );
    }

    return (
        <p className={`${styles.p} ${className}`} {...props}>
            {children}
        </p>
    );
};

export default P;
